import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "motion/react";
import {
  CheckCircle2,
  ChevronRight,
  Loader2,
  Sparkles,
  TriangleAlert,
  Video,
} from "lucide-react";
import { cn } from "../../../lib/utils";
import DocDetailDialog, { type DocTab } from "../../../components/docs/DocDetailDialog";
import { ChatInput } from "../../../components/agno/ChatInput";
import { Badge } from "../../../components/ui/badge";
import { CompanyLogo, getDomainFromCompany } from "../../../components/ui/CompanyLogo";
import { differenceInHours, format, isToday, isTomorrow } from "date-fns";
import { useCurrentUser } from "../../../hooks/QueryHooks";
import { authorizedApiClient } from "../../../services/config";
import { getWorkspaceMeetingDocs, type MeetingDoc } from "../../../services/meetingDocsEndpoint";
import {
  getMeetingDocMarkdown,
  getMeetingDocLabel,
  normalizeMeetingDocStatus,
  type MeetingDocStatus,
} from "../../../services/meetingDocsHelpers";
import { WorkspaceObjectSearchResponse } from "../../../services/workspace/workspaceEndpoints.interface";
import ObjectDetailSheet from "../account-details/account-custom-object/renderers/ObjectDetailSheet";
import TaskDetailSheet from "../tasks/components/TaskDetailSheet";
import type { TaskRecord, BoardStatus } from "../tasks/tasksTypes";
import useWorkspaceStore from "../../../store/workspace/workspaceStore";
import { allWorkspaceMembers } from "../../../services/workspaceMemberEndpoint";

type MemberLookup = Record<string, { name: string; email: string; initials: string }>;

type FeedFilter = "all" | "meetings" | "tasks" | "critical";
type FeedKind = "meeting" | "task";
type FeedPriority = "critical" | "high" | "medium" | "low";

type FeedItem = {
  id: string;
  recordId: string;
  kind: FeedKind;
  title: string;
  subtitle: string;
  timeLabel: string;
  priority: FeedPriority;
  accountName: string;
  accountDomain?: string;
  accountExternalId?: string;
  assignedTo?: string;
  when: Date | null;
  doc?: MeetingDoc | null;
  prepDoc?: MeetingDoc | null;
  followupDoc?: MeetingDoc | null;
  docStatus?: MeetingDocStatus | null;
  docLabel?: string | null;
  docHighlights?: string[];
  docSnippet?: string | null;
  attendees?: Array<{ name: string; company: string }>;
  duration?: string;
  rawFields?: Record<string, any>;
};

const FEED_MEETING_SELECT_FIELDS = [
  "title",
  "name",
  "subject",
  "native_title",
  "description",
  "summary",
  "scheduled_at",
  "start_time",
  "start_at",
  "meeting_start",
  "meeting_date",
  "native_start_time_tz",
  "end_time",
  "native_end_time_tz",
  "created_at",
  "account_name",
  "company",
  "company_name",
  "organization",
  "org_name",
  "company_domain",
  "domain",
  "website",
  "account_domain",
  "account_external_id",
  "organizer",
  "native_organizer_id",
  "attendees",
  "attendee_names",
  "participant_names",
  "participants",
  "participant_emails",
  "native_participants_emails",
  "native_access_recipients",
  "duration",
  "meeting_duration",
  "native_duration",
  "duration_seconds",
  "native_duration_seconds",
  "meeting_url",
  "native_meeting_url",
  "native_source",
  "source",
] as const;

const FEED_TASK_SELECT_FIELDS = [
  "title",
  "name",
  "subject",
  "native_subject",
  "task",
  "description",
  "native_description",
  "details",
  "body_text",
  "native_body_text",
  "evidence_text",
  "status",
  "task_status",
  "native_status",
  "board_status",
  "owner_side",
  "source_type",
  "task_type",
  "priority",
  "task_priority",
  "native_priority",
  "importance",
  "due_date",
  "deadline",
  "scheduled_for",
  "due_date_tz",
  "native_due_date_tz",
  "created_at",
  "created_at_tz",
  "native_created_at_tz",
  "account_name",
  "company",
  "company_name",
  "account_domain",
  "account_external_id",
  "account_arr",
  "account_mrr",
  "account_subscription_status",
  "assigned_to",
  "owner_email",
  "native_owner_email",
  "source",
  "native_source",
  "source_url",
  "native_source_url",
] as const;

const FILTERS: { id: FeedFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "meetings", label: "Meetings" },
  { id: "tasks", label: "Tasks" },
  { id: "critical", label: "Critical" },
];

const PRIORITY_WEIGHT: Record<FeedPriority, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

const PRIORITY_INDICATOR: Record<FeedPriority, string> = {
  critical: "bg-priority-urgent",
  high: "bg-priority-high",
  medium: "bg-priority-medium",
  low: "bg-priority-low",
};

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return "Good Morning";
  if (hour < 17) return "Good Afternoon";
  return "Good Evening";
};

const firstString = (fields: Record<string, any>, keys: string[]) => {
  const match = keys
    .map((key) => fields?.[key])
    .find((value) => (
      (typeof value === "string" && value.trim())
      || typeof value === "number"
    ));

  if (typeof match === "string") return match.trim();
  if (typeof match === "number") return String(match);
  return "";
};

const parseDate = (value: unknown): Date | null => {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;

  if (typeof value === "number") {
    const ms = value > 1e12 ? value : value * 1000;
    const parsed = new Date(ms);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  if (typeof value === "string") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
};

const buildMeetingDocSnippet = (doc: MeetingDoc): string => {
  const markdown = getMeetingDocMarkdown(doc);
  if (typeof markdown === "string" && markdown.trim()) {
    const firstLine = markdown
      .split("\n")
      .map((line) => line.trim())
      .find((line) => line.length > 0);
    if (firstLine) return firstLine.slice(0, 180);
  }
  return "ORA generated an AI brief for this meeting.";
};

const stripMarkdown = (line: string) => (
  line
    .replace(/^\s*#{1,6}\s+/, "")
    .replace(/^\s*[-*+]\s+/, "")
    .replace(/^\s*\d+[.)]\s+/, "")
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1")
    .replace(/[*_`]/g, "")
    .trim()
);

const extractMeetingDocHighlights = (markdown: string | null | undefined, limit = 3): string[] => {
  if (!markdown || typeof markdown !== "string") return [];

  const highlights: string[] = [];
  const seen = new Set<string>();

  markdown
    .split(/\r?\n/)
    .map(stripMarkdown)
    .forEach((line) => {
      if (!line || highlights.length >= limit) return;
      if (line.endsWith(":") && line.length <= 64) return;

      const dedupeKey = line.toLowerCase();
      if (seen.has(dedupeKey)) return;
      seen.add(dedupeKey);
      highlights.push(line);
    });

  return highlights;
};

const cleanAttendeeToken = (value: string): string => (
  value
    .replace(/^[\s"'[\]]+/, "")
    .replace(/[\s"'[\]]+$/, "")
    .trim()
);

const extractAttendeeTokens = (candidate: string): string[] => {
  const raw = candidate.trim();
  if (!raw) return [];

  if (raw.startsWith("[") && raw.endsWith("]")) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => (typeof item === "string" ? cleanAttendeeToken(item) : ""))
          .filter((item): item is string => Boolean(item));
      }
    } catch {
      // fallback to delimiter parsing below
    }
  }

  return raw
    .split(",")
    .map((value) => cleanAttendeeToken(value))
    .filter((item): item is string => Boolean(item));
};

const extractMeetingAttendees = (
  fields: Record<string, any>,
  accountName: string,
): Array<{ name: string; company: string }> => {
  const names: string[] = [];
  const keys = [
    "attendees",
    "attendee_names",
    "participant_names",
    "participants",
    "participant_emails",
    "native_participants_emails",
  ];

  keys.forEach((key) => {
    const candidate = fields[key];
    if (typeof candidate === "string") {
      extractAttendeeTokens(candidate).forEach((value) => {
        if (value) names.push(value);
      });
      return;
    }

    if (Array.isArray(candidate)) {
      candidate.forEach((entry) => {
        if (typeof entry === "string" && entry.trim()) {
          names.push(entry.trim());
        }
      });
    }
  });

  const uniqueNames = Array.from(new Set(names.map((value) => value.toLowerCase())))
    .map((normalized) => names.find((candidate) => candidate.toLowerCase() === normalized))
    .filter((value): value is string => typeof value === "string");

  return uniqueNames.slice(0, 6).map((name) => ({
    name,
    company: accountName || "Company",
  }));
};

const isDefined = <T,>(value: T | null | undefined): value is T =>
  value !== null && value !== undefined;

const readFirstName = (value: unknown): string => {
  if (!value || typeof value !== "object" || !("first_name" in value)) return "there";
  const firstName = value.first_name;
  return typeof firstName === "string" && firstName.trim() ? firstName : "there";
};

const formatWhen = (date: Date | null) => {
  if (!date) return "No date";
  if (isToday(date)) return `Today, ${format(date, "h:mm a")}`;
  if (isTomorrow(date)) return `Tomorrow, ${format(date, "h:mm a")}`;
  return format(date, "MMM d, h:mm a");
};

const inferMeetingPriority = (when: Date | null): FeedPriority => {
  if (!when) return "medium";
  const hoursAway = differenceInHours(when, new Date());
  if (hoursAway >= 0 && hoursAway <= 24) return "critical";
  if (hoursAway > 24 && hoursAway <= 72) return "high";
  if (hoursAway < 0) return "medium";
  return "high";
};

const inferTaskPriority = (fields: Record<string, any>, when: Date | null): FeedPriority => {
  const rawPriority = firstString(fields, ["priority", "task_priority", "importance"]).toLowerCase();

  if (rawPriority.includes("critical") || rawPriority.includes("urgent")) return "critical";
  if (rawPriority.includes("high")) return "high";
  if (rawPriority.includes("low")) return "low";

  if (!when) return "medium";

  const hoursAway = differenceInHours(when, new Date());
  if (hoursAway >= 0 && hoursAway <= 24) return "critical";
  if (hoursAway > 24 && hoursAway <= 72) return "high";
  if (hoursAway < 0) return "medium";

  return "medium";
};

const sortFeedItems = (a: FeedItem, b: FeedItem) => {
  if (PRIORITY_WEIGHT[a.priority] !== PRIORITY_WEIGHT[b.priority]) {
    return PRIORITY_WEIGHT[a.priority] - PRIORITY_WEIGHT[b.priority];
  }

  const aTime = a.when?.getTime() || Number.MAX_SAFE_INTEGER;
  const bTime = b.when?.getTime() || Number.MAX_SAFE_INTEGER;

  return aTime - bTime;
};

const MeetingFeedCard = ({
  item,
  onOpenDoc,
  onClick,
}: {
  item: FeedItem;
  onOpenDoc: (doc: MeetingDoc) => void;
  onClick: () => void;
}) => {
  const attendees = item.attendees || [];
  const accountName = item.accountName || attendees[0]?.company || "Company";
  const isSuccess = item.docStatus === "success";
  const isInProgress = item.docStatus === "pending" || item.docStatus === "running";
  const isFailed = item.docStatus === "failed";

  return (
    <div className="flex gap-4" onClick={onClick} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") onClick(); }}>
      <CompanyLogo
        domain={getDomainFromCompany(accountName)}
        name={accountName}
        className="h-9 w-9"
      />

      <div className="min-w-0 flex-1">
        <div className="mb-0.5 flex items-center justify-between gap-3">
          <h3 className="truncate text-sm font-medium text-foreground">{item.title}</h3>
          <div className="flex flex-shrink-0 items-center gap-1.5">
            {item.priority === "critical" && (
              <Badge variant="statusPill" status="error" className="h-5 px-2 text-[11px]">
                Critical
              </Badge>
            )}
            {isInProgress ? (
              <Badge variant="statusPill" status="pending" className="h-5 px-2 text-[11px]">
                {item.docLabel || "Doc"}
                {" "}
                in progress
              </Badge>
            ) : isSuccess ? (
              <Badge variant="statusPill" status="success" className="h-5 px-2 text-[11px]">
                {item.docLabel || "Doc"}
                {" "}
                ready
              </Badge>
            ) : isFailed ? (
              <Badge variant="statusPill" status="error" className="h-5 px-2 text-[11px]">
                {item.docLabel || "Doc"}
                {" "}
                failed
              </Badge>
            ) : null}
          </div>
        </div>

        <div className="mb-3 flex items-center gap-2 text-[13px] text-muted-foreground">
          <span>{item.timeLabel}</span>
          {item.duration && (
            <>
              <span className="text-border">·</span>
              <span>{item.duration}</span>
            </>
          )}
          {accountName && (
            <>
              <span className="text-border">·</span>
              <span className="flex items-center gap-1.5">
                <Video className="h-3 w-3" />
                {accountName}
              </span>
            </>
          )}
        </div>

        {attendees.length > 0 && (
          <div className="mb-3 flex items-center gap-2">
            <div className="flex -space-x-1.5">
              {attendees.slice(0, 3).map((attendee) => (
                <div
                  key={`${item.id}-${attendee.name}`}
                  className="flex h-6 w-6 items-center justify-center rounded-full bg-muted-foreground text-[10px] font-medium text-white ring-2 ring-background"
                  title={`${attendee.name} - ${attendee.company}`}
                >
                  {attendee.name.split(" ").map((token) => token[0]).join("")}
                </div>
              ))}
            </div>
            <span className="text-[13px] text-muted-foreground">
              {attendees.map((attendee) => attendee.name.split(" ")[0]).join(", ")}
            </span>
          </div>
        )}

        {item.subtitle ? (
          <p className="mb-3 line-clamp-1 text-[13px] text-muted-foreground">{item.subtitle}</p>
        ) : null}

        {isSuccess && item.doc ? (
          <div className="rounded-lg border border-border bg-quaternary p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5">
                <Sparkles className="h-3.5 w-3.5 text-primary" />
                <span className="text-[11px] font-medium text-foreground">
                  {item.docLabel || "AI"}
                  {" "}
                  Brief
                </span>
              </div>
              <button
                type="button"
                className="text-[11px] font-medium text-primary transition-colors hover:text-primary/80"
                onClick={(e) => { e.stopPropagation(); onOpenDoc(item.doc as MeetingDoc); }}
              >
                Open brief
              </button>
            </div>
            {item.docHighlights && item.docHighlights.length > 0 ? (
              <ul className="space-y-1">
                {item.docHighlights.map((insight) => (
                  <li key={`${item.id}-${insight}`} className="flex items-start gap-2 text-[13px] text-muted-foreground">
                    <span className="mt-2 h-1 w-1 flex-shrink-0 rounded-full bg-muted-foreground/40" />
                    {insight}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-[13px] text-muted-foreground">
                {item.docSnippet || "ORA generated an AI brief for this meeting."}
              </p>
            )}
          </div>
        ) : isInProgress ? (
          <div className="rounded-lg border border-border bg-quaternary p-3">
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-600" />
                <span className="text-[11px] font-medium text-foreground">
                  {item.docLabel || "Doc"}
                  {" "}
                  in progress
                </span>
              </div>
              {item.doc && (
                <button
                  type="button"
                  className="text-[11px] font-medium text-primary transition-colors hover:text-primary/80"
                  onClick={(e) => { e.stopPropagation(); onOpenDoc(item.doc as MeetingDoc); }}
                >
                  Open brief
                </button>
              )}
            </div>
            <p className="text-[13px] text-muted-foreground">ORA is preparing this brief in the background.</p>
          </div>
        ) : isFailed ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3">
            <div className="mb-1.5 flex items-center justify-between gap-2">
              <div className="flex items-center gap-1.5">
                <TriangleAlert className="h-3.5 w-3.5 text-red-600" />
                <span className="text-[11px] font-medium text-red-700">
                  {item.docLabel || "Doc"}
                  {" "}
                  failed
                </span>
              </div>
              {item.doc && (
                <button
                  type="button"
                  className="text-[11px] font-medium text-primary transition-colors hover:text-primary/80"
                  onClick={(e) => { e.stopPropagation(); onOpenDoc(item.doc as MeetingDoc); }}
                >
                  Open brief
                </button>
              )}
            </div>
            <p className="text-[13px] text-red-700">
              {item.doc?.error_message || "ORA could not generate this doc yet. Try again."}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
};

const TASK_PRIORITY_COLORS: Record<string, string> = {
  critical: "bg-red-500",
  high: "bg-red-500",
  medium: "bg-amber-500",
  low: "bg-blue-500",
};

const TaskFeedCard = ({ item, onClick, memberLookup }: { item: FeedItem; onClick: () => void; memberLookup: MemberLookup }) => {
  const accountDomain = item.accountDomain || (item.accountName ? getDomainFromCompany(item.accountName) : "");
  const priorityDot = TASK_PRIORITY_COLORS[item.priority];
  const assignee = item.assignedTo
    ? (memberLookup[item.assignedTo] || {
        name: item.assignedTo,
        email: item.assignedTo,
        initials: item.assignedTo.charAt(0).toUpperCase(),
      })
    : null;

  return (
    <div className="flex gap-4" onClick={onClick} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter") onClick(); }}>
      <div className="mt-0.5 flex-shrink-0">
        <CheckCircle2 className="h-5 w-5 text-status-active" />
      </div>

      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex items-center justify-between gap-2">
          <h3 className="truncate text-sm font-medium text-foreground">{item.title}</h3>
          <div className="flex items-center gap-2 flex-shrink-0">
            {(item.priority === "critical" || item.priority === "high") && (
              <Badge variant="statusPill" status="error" className="h-5 px-2 text-[11px]">
                {item.priority === "critical" ? "Critical" : "High"}
              </Badge>
            )}
          </div>
        </div>

        {item.subtitle && item.subtitle !== "Task" && item.subtitle !== item.title && (
          <p className="line-clamp-1 text-[13px] text-muted-foreground">{item.subtitle}</p>
        )}

        <div className="flex items-center gap-2 pt-0.5">
          {/* Priority dot */}
          {priorityDot && (
            <span
              className={`h-2.5 w-2.5 rounded-full flex-shrink-0 ${priorityDot}`}
              title={`Priority: ${item.priority}`}
            />
          )}

          {/* Time label */}
          <span className="text-xs text-muted-foreground">{item.timeLabel}</span>

          {/* Account */}
          {item.accountName && (
            <>
              <span className="text-border">·</span>
              <CompanyLogo
                domain={accountDomain}
                name={item.accountName}
                className="h-4 w-4 rounded flex-shrink-0"
              />
              <span className="truncate text-xs text-muted-foreground max-w-[140px]">
                {item.accountName}
              </span>
            </>
          )}

          <div className="flex-1" />

          {/* Assignee */}
          {assignee ? (
            <div className="flex items-center gap-1 flex-shrink-0">
              <div className="flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground">
                {assignee.initials}
              </div>
              <span className="text-xs text-muted-foreground truncate max-w-[80px]">
                {assignee.name}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-1 flex-shrink-0">
              <div className="flex h-5 w-5 items-center justify-center rounded-full border border-dashed border-muted-foreground/30 text-[10px] text-muted-foreground/40">
                ?
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const feedItemToTaskRecord = (item: FeedItem): TaskRecord => {
  const f = item.rawFields || {};
  return {
    id: item.recordId,
    external_id: item.recordId,
    subject: firstString(f, ["subject", "native_subject", "title", "name", "task"]) || undefined,
    native_subject: f.native_subject || undefined,
    description: firstString(f, ["description", "details"]) || undefined,
    native_description: f.native_description || undefined,
    status: firstString(f, ["status", "task_status", "native_status"]) || "open",
    priority: firstString(f, ["priority", "task_priority", "native_priority", "importance"]) || "medium",
    board_status: (f.board_status || "investigating") as BoardStatus,
    owner_side: f.owner_side || undefined,
    due_date: f.due_date || f.deadline || f.scheduled_for || undefined,
    assigned_to: item.assignedTo || undefined,
    evidence_text: f.evidence_text || undefined,
    source_type: f.source_type || undefined,
    task_type: f.task_type || undefined,
    account_name: item.accountName || undefined,
    account_domain: item.accountDomain || undefined,
    account_external_id: item.accountExternalId || undefined,
    account_arr: f.account_arr || undefined,
    account_mrr: f.account_mrr || undefined,
    account_subscription_status: f.account_subscription_status || undefined,
    created_at: f.created_at || f.created_at_tz || f.native_created_at_tz || undefined,
  };
};

const getField = (fields: Record<string, any>, ...keys: string[]): string => {
  for (const k of keys) {
    if (fields[k]) return fields[k];
  }
  return "";
};

const HeadquartersPage = () => {
  const workspace = useWorkspaceStore((state) => state.workspace);
  const navigate = useNavigate();

  const membersQuery = useQuery({
    queryKey: ["workspace-members", workspace?.id],
    queryFn: () => allWorkspaceMembers({ workspace_id: workspace!.id }),
    enabled: !!workspace?.id,
    staleTime: 60_000,
  });
  const memberLookup = useMemo<MemberLookup>(() => {
    const map: MemberLookup = {};
    const members: any[] = (membersQuery.data as any)?.data?.members ?? [];
    for (const m of members) {
      const name = `${m.first_name || ""} ${m.last_name || ""}`.trim() || m.email;
      const initials = (m.first_name?.[0] || m.email?.[0] || "?").toUpperCase();
      const entry = { name, email: m.email, initials };
      // assigned_to can be a member_id OR a user_id (extracted tasks use user_id) — index both, plus email
      if (m.member_id) map[m.member_id] = entry;
      if (m.user_id) map[m.user_id] = entry;
      if (m.email) map[m.email] = entry;
    }
    return map;
  }, [membersQuery.data]);

  const [activeFilter, setActiveFilter] = useState<FeedFilter>("all");
  const [docDialogState, setDocDialogState] = useState<{ item: FeedItem; initialTab: DocTab } | null>(null);
  const [selectedFeedItem, setSelectedFeedItem] = useState<FeedItem | null>(null);

  const currentUser = useCurrentUser({
    enabled: !!workspace?.id,
  });

  const feedQuery = useQuery<FeedItem[]>({
    queryKey: ["headquarters-feed", workspace?.id],
    enabled: !!workspace?.id,
    queryFn: async () => {
      const workspaceId = workspace?.id;
      if (!workspaceId) return [];

      const searchObject = async (
        objectType: string,
        limit = 20,
        select?: readonly string[],
      ) => {
        try {
          const response = await authorizedApiClient.post<WorkspaceObjectSearchResponse>(
            `/workspace/${workspaceId}/object-search`,
            {
              object_type: objectType,
              select: select ? [...select] : undefined,
              limit,
              sort: [{ field: "created_at", order: "desc" }],
            },
          );

          return response?.data?.data || [];
        } catch (error) {
          // Tasks/meetings may be disabled in some workspaces.
          return [];
        }
      };

      const [meetingRecords, taskRecords, prepDocs, followupDocs] = await Promise.all([
        searchObject("meetings", 12, FEED_MEETING_SELECT_FIELDS),
        searchObject("tasks", 12, FEED_TASK_SELECT_FIELDS),
        getWorkspaceMeetingDocs({
          workspace_id: workspaceId,
          object_type: "meetings",
          doc_type: "meeting_prep",
          latest_per_object: true,
          limit: 50,
        }).then((response) => response.docs || []).catch(() => []),
        getWorkspaceMeetingDocs({
          workspace_id: workspaceId,
          object_type: "meetings",
          doc_type: "meeting_followup",
          latest_per_object: true,
          limit: 50,
        }).then((response) => response.docs || []).catch(() => []),
      ]);

      const prepDocByMeeting = new Map<string, MeetingDoc>();
      prepDocs.forEach((doc) => {
        if (!doc.object_external_id || prepDocByMeeting.has(doc.object_external_id)) return;
        prepDocByMeeting.set(doc.object_external_id, doc);
      });

      const followupDocByMeeting = new Map<string, MeetingDoc>();
      followupDocs.forEach((doc) => {
        if (!doc.object_external_id || followupDocByMeeting.has(doc.object_external_id)) return;
        followupDocByMeeting.set(doc.object_external_id, doc);
      });

      const meetings = meetingRecords.map((record) => {
        const fields = record?.fields || {};
        const accountName = firstString(fields, ["account_name", "company", "company_name", "organization", "org_name"]);
        const when = parseDate(
          fields.scheduled_at
          || fields.start_time
          || fields.start_at
          || fields.meeting_start
          || fields.meeting_date
          || fields.native_start_time_tz
          || fields.created_at,
        );
        const isUpcomingMeeting = when ? when.getTime() > Date.now() : true;
        const preferredDoc = isUpcomingMeeting
          ? prepDocByMeeting.get(record.id)
          : followupDocByMeeting.get(record.id);
        const fallbackDoc = isUpcomingMeeting
          ? followupDocByMeeting.get(record.id)
          : prepDocByMeeting.get(record.id);
        const selectedMeetingDoc = preferredDoc || fallbackDoc || null;
        const docStatus = selectedMeetingDoc
          ? normalizeMeetingDocStatus(selectedMeetingDoc.status)
          : null;
        const docLabel = selectedMeetingDoc
          ? getMeetingDocLabel(selectedMeetingDoc.doc_type)
          : null;
        const docMarkdown = selectedMeetingDoc
          ? getMeetingDocMarkdown(selectedMeetingDoc)
          : null;
        const docHighlights = extractMeetingDocHighlights(docMarkdown, 3);
        const docSnippet = selectedMeetingDoc
          ? buildMeetingDocSnippet(selectedMeetingDoc)
          : null;

        const defaultSubtitle = firstString(fields, ["description", "summary"])
          || (isUpcomingMeeting ? "Upcoming meeting" : "Past meeting");
        const docSubtitle = selectedMeetingDoc
          ? (
            docStatus === "success"
              ? `${docLabel} ready • ${(docHighlights[0] || docSnippet || "AI brief available").slice(0, 180)}`
              : docStatus === "failed"
                ? `${docLabel} failed • ${selectedMeetingDoc.error_message || "Try again."}`
                : `${docLabel} in progress • ORA is working in the background.`
          )
          : defaultSubtitle;
        const attendees = extractMeetingAttendees(fields, accountName);
        const duration = firstString(fields, ["duration", "meeting_duration", "native_duration"]);

        const accountDomain = firstString(fields, ["account_domain", "company_domain", "domain", "website"]);
        const accountExternalId = firstString(fields, ["account_external_id"]);

        const meetingItem: FeedItem = {
          id: `meeting-${record.id}`,
          recordId: record.id,
          kind: "meeting",
          title: firstString(fields, ["title", "name", "subject", "native_title"]) || "Untitled meeting",
          subtitle: docSubtitle,
          accountName,
          accountDomain: accountDomain || undefined,
          accountExternalId: accountExternalId || undefined,
          when,
          timeLabel: formatWhen(when),
          priority: inferMeetingPriority(when),
          doc: selectedMeetingDoc,
          prepDoc: prepDocByMeeting.get(record.id) || null,
          followupDoc: followupDocByMeeting.get(record.id) || null,
          docStatus,
          docLabel,
          docHighlights,
          docSnippet,
          attendees,
          duration: duration || undefined,
          rawFields: fields,
        };
        return meetingItem;
      });

      const tasks = taskRecords
        .map((record) => {
          const fields = record?.fields || {};
          const status = firstString(fields, ["status", "task_status"]).toLowerCase();

          if (status === "done" || status === "completed" || status === "closed") {
            return null;
          }

          const when = parseDate(
            fields.due_date
            || fields.deadline
            || fields.scheduled_for
            || fields.created_at,
          );

          const taskItem: FeedItem = {
            id: `task-${record.id}`,
            recordId: record.id,
            kind: "task",
            title: firstString(fields, ["subject", "native_subject", "title", "name", "task"]) || "Untitled task",
            subtitle: firstString(fields, ["description", "details", "body_text", "native_body_text"]) || "Task",
            accountName: firstString(fields, ["account_name", "company", "company_name"]),
            accountDomain: firstString(fields, ["account_domain"]) || undefined,
            accountExternalId: firstString(fields, ["account_external_id"]) || undefined,
            assignedTo: firstString(fields, ["assigned_to", "owner_email", "native_owner_email"]) || undefined,
            when,
            timeLabel: when ? `Due ${formatWhen(when)}` : "No due date",
            priority: inferTaskPriority(fields, when),
            rawFields: fields,
          };
          return taskItem;
        })
        .filter(isDefined);

      return [...meetings, ...tasks].sort(sortFeedItems).slice(0, 20);
    },
  });

  const allItems = feedQuery.data || [];

  const criticalCount = useMemo(
    () => allItems.filter((item) => item.priority === "critical").length,
    [allItems],
  );

  const filteredItems = useMemo(() => {
    if (activeFilter === "all") return allItems;
    if (activeFilter === "critical") return allItems.filter((item) => item.priority === "critical");
    if (activeFilter === "meetings") return allItems.filter((item) => item.kind === "meeting");
    if (activeFilter === "tasks") return allItems.filter((item) => item.kind === "task");
    return allItems;
  }, [allItems, activeFilter]);

  const firstName = readFirstName(currentUser.data);

  const onSendMessage = useCallback(
    (message: string | FormData) => {
      if (!workspace?.unique_identifier) return;
      const text = typeof message === "string" ? message : (message.get("message") as string) ?? "";
      if (!text.trim()) return;

      navigate(`/home/${workspace.unique_identifier}/ai-chat`, {
        state: { initialMessage: text },
      });
    },
    [workspace?.unique_identifier, navigate],
  );

  return (
    <div className="min-h-[calc(100vh-86px)] bg-background px-6 pb-10">
      <div className="mx-auto max-w-5xl">
        <section className="pb-10 pt-14 text-center">
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">
            {getGreeting()}, <span className="text-primary">{firstName}</span>
          </h1>
          <p className="mx-auto mt-3 max-w-xl text-sm text-muted-foreground">
            Ready to grow customer relationships and drive meaningful engagement.
          </p>

          <div className="mx-auto mt-8 max-w-2xl">
            <ChatInput
              onSendMessage={onSendMessage}
              isReady={true}
              placeholder="Ask ORA about your accounts, meetings, or risks..."
              variant="ghost"
            />
          </div>
        </section>

        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
          className="overflow-hidden rounded-lg border border-border bg-background"
        >
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <div className="flex items-center gap-3">
              <h2 className="text-base font-semibold text-foreground">Feed</h2>
              {criticalCount > 0 && (
                <Badge variant="statusPill" status="error">
                  {criticalCount}
                  {" "}
                  critical
                </Badge>
              )}
            </div>
            <button
              type="button"
              className="inline-flex items-center gap-0.5 text-sm font-medium text-primary transition-colors hover:text-primary/80"
              onClick={() => {
                if (!workspace?.unique_identifier) return;
                navigate(`/home/${workspace.unique_identifier}/inbox`);
              }}
            >
              View all
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          <div className="flex items-center gap-1 border-b border-border px-5 py-3">
            {FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                onClick={() => setActiveFilter(filter.id)}
                className={cn(
                  "rounded-[5px] px-3 py-1.5 text-sm font-medium transition-all",
                  activeFilter === filter.id
                    ? "bg-foreground text-background"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground",
                )}
              >
                {filter.label}
              </button>
            ))}
          </div>

          {feedQuery.isLoading ? (
            <div className="flex items-center justify-center gap-2 px-5 py-12 text-center text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading feed...
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="px-5 py-12 text-center text-sm text-muted-foreground">
              No feed items yet.
            </div>
          ) : (
            <div className="divide-y divide-border">
              <AnimatePresence mode="popLayout">
                {filteredItems.map((item, index) => (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ delay: index * 0.02 }}
                    className="group relative cursor-pointer px-5 py-4 transition-colors hover:bg-muted"
                  >
                    <div
                      className={cn(
                        "absolute bottom-4 left-0 top-4 w-[3px] rounded-r-full",
                        PRIORITY_INDICATOR[item.priority],
                      )}
                    />
                    {item.kind === "meeting" ? (
                      <MeetingFeedCard
                        item={item}
                        onOpenDoc={(doc) => {
                          const initialTab: DocTab =
                            doc.doc_type === "meeting_followup" ? "followup"
                            : doc.doc_type === "meeting_prep" ? "prep"
                            : "custom";
                          setDocDialogState({ item, initialTab });
                        }}
                        onClick={() => setSelectedFeedItem(item)}
                      />
                    ) : (
                      <TaskFeedCard item={item} onClick={() => setSelectedFeedItem(item)} memberLookup={memberLookup} />
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          )}
        </motion.section>
      </div>
      <DocDetailDialog
        open={Boolean(docDialogState)}
        onOpenChange={(open) => { if (!open) setDocDialogState(null); }}
        title={docDialogState?.item.title}
        prepDoc={docDialogState?.item.prepDoc}
        followupDoc={docDialogState?.item.followupDoc}
        initialTab={docDialogState?.initialTab}
      />
      {selectedFeedItem && selectedFeedItem.kind === "task" && (
        <TaskDetailSheet
          task={feedItemToTaskRecord(selectedFeedItem)}
          open={true}
          onClose={() => setSelectedFeedItem(null)}
          memberLookup={memberLookup}
          members={(membersQuery.data as any)?.data?.members ?? []}
        />
      )}
      {selectedFeedItem && selectedFeedItem.kind === "meeting" && (
        <ObjectDetailSheet
          open={true}
          onClose={() => setSelectedFeedItem(null)}
          title={selectedFeedItem.title}
          subtitle={selectedFeedItem.timeLabel}
          badge={
            selectedFeedItem.when && selectedFeedItem.when.getTime() > Date.now()
              ? { label: "Upcoming" }
              : { label: "Past" }
          }
          sourceUrl={getField(selectedFeedItem.rawFields || {}, "meeting_url", "native_meeting_url")}
          sourceLabel="Join Meeting"
          account={
            selectedFeedItem.accountName
              ? {
                  name: selectedFeedItem.accountName,
                  domain: selectedFeedItem.accountDomain,
                  externalId: selectedFeedItem.accountExternalId,
                }
              : undefined
          }
          fields={(() => {
            const f = selectedFeedItem.rawFields || {};
            return [
              { label: "Organizer", value: getField(f, "organizer", "native_organizer_id", "native_organizer_email") },
              { label: "Duration", value: selectedFeedItem.duration || getField(f, "duration", "meeting_duration", "native_duration") },
              { label: "Start", value: getField(f, "start_time", "native_start_time_tz", "scheduled_at", "start_at", "meeting_start", "meeting_date"), type: "date" as const },
              { label: "End", value: getField(f, "end_time", "native_end_time_tz"), type: "date" as const },
              { label: "Source", value: getField(f, "source", "native_source") },
              {
                label: "Participants",
                type: "participants" as const,
                value: selectedFeedItem.attendees?.map((a) => a.name)
                  || f.attendees || f.native_participants_emails || f.participant_emails
                  || f.participants || f.attendee_names || f.participant_names || [],
              },
            ];
          })()}
          bodyMarkdown={
            (selectedFeedItem.doc ? getMeetingDocMarkdown(selectedFeedItem.doc) : null)
              || getField(selectedFeedItem.rawFields || {}, "description", "summary")
              || undefined
          }
          bodyLabel={selectedFeedItem.doc ? (getMeetingDocLabel(selectedFeedItem.doc.doc_type) + " Brief") : "Description"}
        />
      )}
    </div>
  );
};

export default HeadquartersPage;
